import { useEffect, useRef, useState } from 'react';

const sleep = ms => new Promise(r => setTimeout(r, ms));

/**
 * Animates a 0–100% progress value in sync with an async submit.
 * Use with a progress bar UI while isSubmitting is true.
 *
 * @param {boolean} isSubmitting - True while the submit (e.g. API call) is in progress
 * @param {{ resetWhen?: boolean }} [options] - When resetWhen is false (e.g. modal closed), progress and status reset to idle
 * @returns {{ progress: number, status: 'idle' | 'loading' | 'done' }}
 */
export function useSubmitProgress(isSubmitting, options = {}) {
  const { resetWhen = true } = options;
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle');
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    isSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  useEffect(() => {
    if (resetWhen === false) {
      setProgress(0);
      setStatus('idle');
    }
  }, [resetWhen]);

  useEffect(() => {
    if (!isSubmitting) {
      if (status === 'loading') setStatus('done');
      return;
    }

    setStatus('loading');
    setProgress(0);

    let cancelled = false;
    const run = async () => {
      for (let i = 0; i <= 20; i += 4) {
        if (cancelled || !isSubmittingRef.current) return;
        setProgress(i);
        await sleep(60);
      }
      for (let i = 24; i <= 90; i += 2) {
        if (cancelled || !isSubmittingRef.current) return;
        setProgress(i);
        await sleep(80);
      }
      while (!cancelled && isSubmittingRef.current) {
        await sleep(100);
      }
      if (cancelled) return;
      for (let i = 92; i <= 100; i += 2) {
        setProgress(i);
        await sleep(30);
      }
      setStatus('done');
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isSubmitting]);

  useEffect(() => {
    if (status !== 'done' || progress >= 100) return;
    const id = setInterval(() => {
      setProgress(p => Math.min(p + 4, 100));
    }, 30);
    return () => clearInterval(id);
  }, [status, progress]);

  return { progress, status };
}
